import { useCallback, useEffect, useState } from "react";
import Auth from "../Auth";

type Collision = {
  stripeCustomerId: string;
  stripeEmail: string;
  stripeCustomerName: string | null;
  created: number;
  sudowriteUsers: Array<{
    uid: string;
    email: string;
    displayName: string | null;
  }>;
};

type StripeEmailCollisionWarningProps = {
  user: any;
};

export default function StripeEmailCollisionWarning({
  user,
}: StripeEmailCollisionWarningProps) {
  const [collisions, setCollisions] = useState<Collision[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchCollisions = useCallback(async () => {
    const stripeCustomerId = user?.subscription?.data?.stripeCustomerId;
    if (!stripeCustomerId) return;

    setLoading(true);
    try {
      const res = await Auth.fetchWithIdToken(
        `/api/admin/stripe-email-collisions/${stripeCustomerId}`,
        { method: "POST" }
      );
      if (res.success && res.collisions) {
        setCollisions(res.collisions);
      }
    } catch (e) {
      console.error("[StripeEmailCollisionWarning] fetch error", e);
    } finally {
      setLoading(false);
    }
  }, [user?.subscription?.data?.stripeCustomerId]);

  useEffect(() => {
    fetchCollisions();
  }, [fetchCollisions]);

  if (!user?.subscription?.data?.stripeCustomerId) return null;
  if (loading) return null;
  if (collisions.length === 0) return null;

  return (
    <div className="my-5 rounded-lg p-4 border border-app-danger-color bg-red-50">
      <h4 className="text-lg font-bold text-red-700 mb-3">
        ⚠️ Stripe Email Collision Detected
      </h4>
      <p className="text-sm text-red-600 mb-3">
        Other Stripe customers share the same email address but are linked to
        different Sudowrite accounts.
      </p>
      <table className="w-full border border-app-danger-color bg-white">
        <thead>
          <tr className="border-b border-app-danger-color bg-red-200">
            <th className="text-left p-2">Stripe Customer ID</th>
            <th className="text-left p-2">Stripe Email</th>
            <th className="text-left p-2">Customer Name</th>
            <th className="text-left p-2">Linked Sudowrite Account(s)</th>
          </tr>
        </thead>
        <tbody>
          {collisions.map((collision) => (
            <tr
              key={collision.stripeCustomerId}
              className="border-b border-red-200 bg-red-100"
            >
              <td className="p-2 text-sm font-mono">
                {collision.stripeCustomerId}
              </td>
              <td className="p-2 text-sm">{collision.stripeEmail}</td>
              <td className="p-2 text-sm">
                {collision.stripeCustomerName || "—"}
              </td>
              <td className="p-2 text-sm">
                {collision.sudowriteUsers.length === 0 ? (
                  <span className="text-gray-500 italic">
                    No linked Sudowrite account
                  </span>
                ) : (
                  collision.sudowriteUsers.map((su) => (
                    <div key={su.uid}>
                      <a
                        href={`/admin/user/${su.email}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        {su.email}
                      </a>
                      {su.displayName ? (
                        <span className="text-gray-500 ml-1">
                          ({su.displayName})
                        </span>
                      ) : null}
                    </div>
                  ))
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
